import React from 'react';
import { observer, Provider } from 'mobx-react';
import { Route, Switch } from 'react-router';
import { CourseListPage } from './containers/courses/CourseListPage';
import './mainv3.module.scss';
import { CourseShowPage } from './containers/courses/CourseShowPage';
import { Navigation } from './containers/navigation/Navigation';
import { HomePage } from './containers/home/HomePage';
import { ApplicationPage } from './containers/application/ApplicationPage';
import { observable, makeObservable } from 'mobx';
import { NavigationState, NavigationType } from './states/NavigationState';
import { SignOutPage } from '../mainv2/containers/sign_out/SignOutPage';
import { SignInPage } from './containers/auth/SignInPage';
import { CoachSkillCertificationState } from './states/CoachSkillCertificationState';
import { TimesheetPage } from './containers/timesheet/TimesheetPage';
import { MigrationPage } from './containers/migration/MigrationPage';
import { WritingSectionPage } from './containers/course_exams/WritingSectionPage';
import { PracticalSectionPage } from './containers/course_exams/PracticalSectionPage';
import { CourseCompletionCertificatePage } from './containers/courses/CourseCompletionCertificatePage';
import { ExamPracticalSubmissionsPage } from './containers/exam_practical_submissions/ExamPracticalSubmissionsPage';
import { ReceiptView } from './containers/receipts/ReceiptView';
import { GlobalStatesV3 } from '../commonv3/states/GlobalStatesV3';
import { Notifier } from './components/atoms/notifications/Notifier';
import { MembershipPlanUpdatePage } from './containers/membership_plans/MembershipPlanUpdatePage';
import { Footer } from './components/organisms/footer/Footer';
import { FaqPage } from './containers/faq/FaqPage';
import { EventArchivePage } from './containers/events/EventArchivePage';
import { CustomApplicationPage } from './containers/application/CustomApplicationPage';
import { EventDetailsPage } from '../mainv2/containers/event/EventDetailsPage';
import { EventReservationPage } from '../mainv2/containers/event_reservation/EventReservationPage';
import { PricingPage } from './containers/pricing/PricingPage';
import { HomeLocationUpdatePage } from './containers/home_locations/HomeLocationUpdatePage';
import { AccessPage } from './containers/access/AccessPage';
import { AttendancePage } from './containers/attendances/AttendancePage';
import { EventReservationAttendanceCompletePage } from './containers/attendances/EventReservationAttendanceCompletePage';
import { ReservationAttendanceCompletePage } from './containers/attendances/ReservationAttendanceCompletePage';
import { SurveyShowPage } from './containers/surveys/SurveyShowPage';
import { WithdrawalCancelPage } from './containers/withdrawal/WithdrawalCancelPage';
import { SubscriptionSuspensionInfoPage } from './containers/subscription_suspensions/SubscriptionSuspensionInfoPage';
import { SubscriptionSuspensionCreatePage } from './containers/subscription_suspensions/SubscriptionSuspensionCreatePage';
import { SubscriptionSuspensionConfirmationPage } from './containers/subscription_suspensions/SubscriptionSuspensionConfirmationPage';
import { WithdrawalApplyPage } from './containers/withdrawal/WithdrawalApplyPage';
import { SubscriptionSuspensionCancelInfoPage } from './containers/subscription_suspensions/SubscriptionSuspensionCancelInfoPage';
import { WithdrawalSurveyShowPage } from './containers/withdrawal/WithdrawalSurveryPage';
import { WithdrawalCompletePage } from './containers/withdrawal/WithdrawalCompletePage';
import { ResetPasswordPage } from './containers/passwords/ResetPasswordPage';
import { LessonHomeworkSubmissionPage } from './containers/lesson_homeworks/LessonHomeworkSubmissionPage';
import { LessonHomeworkSubmissionCompletePage } from './containers/lesson_homeworks/LessonHomeworkSubmissionCompletePage';
import { LessonHomeworkSubmissionShowPage } from './containers/lesson_homeworks/LessonHomeworkSubmissionShowPage';
import { LessonHomeworkSubmissionShowPageForReviewer } from './containers/reviewer/LessonHomeworkSubmissionShowPageForReviewer';
import { UserCourseWorkSubmissionListPage } from './containers/course_work_submissions/UserCourseWorkSubmissionListPage';
import { CourseWorkSubmissionListPage } from './containers/reviewer/CourseWorkSubmissionListPage';
import { LessonHomeWorkSubmissionListPage } from './containers/lesson_homeworks/LessonHomeWorkSubmissionListPage';
import { PracticalSectionSubmissionPage } from './containers/course_exams/PracticalSectionSubmissionPage';
import { ExamPracticalSectionSubmissionCompletePage } from './containers/course_exams/ExamPracticalSectionSubmissionCompletePage';
import { ExamPracticalSubmissionShowPage } from './containers/course_exams/ExampPracticalSectionSubmissionShowPage';
import { ExamPracticalSectionShowPageForReviewer } from './containers/reviewer/ExamPracticalSectionSubmissionShowPageForReviewer';
import { NotificationDeliveryListPage } from './containers/notification_deliveries/NotificationDeliveryListPage';
import { MultiCreatorFullPage } from './containers/multi_creator/MultiCreatorFullPage';
import { NotificationDeliveryShowPage } from './containers/notification_deliveries/NotificationDeliveryShowPage';
import { MulticreatorApplicationPage } from './containers/application/MulticreatorApplicationPage';
import { BeautyApplicationPage } from './containers/application/BeautyApplicationPage';
import { MoneyApplicationPage } from './containers/application/MoneyApplicationPage';
import { DesignerPage } from './containers/course_set/DesignerPage';
import { MarketerPage } from './containers/course_set/MarketerPage';
import { WriterPage } from './containers/course_set/WriterPage';
import { PopularPage } from './containers/course_set/PopularPage';
import { GlobalStates } from '../mainv2/states/globalStates';
import { routingStore } from '../common/stores/routing';
import { EventsPage } from './containers/events/EventsPage';
import { CoachingPage } from './containers/coaching/CoachingPage';
import { DesktopHeader } from './containers/navigation/DesktopHeader';
import { SheCreatorsPage } from './containers/she_creators/SheCreatorsPage';
import { MokumokuPage } from './containers/mokumoku/MokumokuPage';
import classNames from 'classnames';

const RedirectToRoot = (props: { isSignedIn: boolean }) => {
  if (props.isSignedIn) {
    routingStore.replace('/home');
  } else {
    window.location.replace('/');
  }
  return null;
};

const MainAppV3Routes = () => {
  const basePath = '';
  const isSignedIn = GlobalStates.currentUser.isSignedIn();
  return (
    <Switch>
      <Route exact path={`${basePath}/events/:eventId`} component={EventDetailsPage} />
      <Route exact path={`${basePath}/events/:eventId/reservation`} component={EventReservationPage} />
      <Route exact path={`${basePath}/sign_in`} component={SignInPage} />
      <Route exact path={`${basePath}/sign_out`} component={SignOutPage} />
      <Route path={`${basePath}/passwords`} component={ResetPasswordPage} />
      <Route exact path={`${basePath}/full/multicreators`} component={MultiCreatorFullPage} />

      <Route exact path={`${basePath}/access`} component={AccessPage} />
      <Route exact path={`${basePath}/`} component={HomePage} />
      <Route exact path={`${basePath}/events`} component={EventsPage} />
      <Route path={`${basePath}/home`} component={HomePage} />
      <Route exact path={`${basePath}/courses`} component={CourseListPage} />
      <Route path={`${basePath}/coaching`} component={CoachingPage} />
      <Route path={`${basePath}/she_creators`} component={SheCreatorsPage} />
      <Route path={`${basePath}/mokumoku`} component={MokumokuPage} />
      <Route path={`${basePath}/event_archives`} component={EventArchivePage} />

      <Route
        exact
        path={`${basePath}/courses/:courseId/completion_certificate`}
        component={CourseCompletionCertificatePage}
      />
      <Route path={`${basePath}/courses/:courseId`} component={CourseShowPage} />
      <Route path={`${basePath}/course/set/web-designer`} component={DesignerPage} />
      <Route path={`${basePath}/course/set/marketer`} component={MarketerPage} />
      <Route path={`${basePath}/course/set/writer`} component={WriterPage} />
      <Route path={`${basePath}/course/set/popular`} component={PopularPage} />
      <Route
        path={`${basePath}/lesson_homeworks/:homeworkId/submissions/new`}
        component={LessonHomeworkSubmissionPage}
      />
      <Route
        path={`${basePath}/lesson_homework_submissions/:submissionToken`}
        component={LessonHomeworkSubmissionShowPage}
      />
      <Route path={`${basePath}/course_work_submissions`} component={UserCourseWorkSubmissionListPage} />
      <Route
        path={`${basePath}/lesson_homeworks/:homeworkId/submissions/complete`}
        component={LessonHomeworkSubmissionCompletePage}
      />
      <Route
        path={`${basePath}/lesson_homeworks/:lessonHomeWorkId/submissions`}
        component={LessonHomeWorkSubmissionListPage}
      />
      <Route
        path={`${basePath}/exam_practical_section_submissions/:submissionToken`}
        component={ExamPracticalSubmissionShowPage}
      />
      <Route
        exact
        path={`${basePath}/exam_practical_submissions/:submission_code`}
        component={ExamPracticalSubmissionsPage}
      />
      <Route path={`${basePath}/exam_writing_sections/:sectionId`} component={WritingSectionPage} />
      <Route
        path={`${basePath}/exam_practical_sections/:examPracticalSectionId/submissions/complete`}
        component={ExamPracticalSectionSubmissionCompletePage}
      />
      <Route
        path={`${basePath}/exam_practical_sections/:examPracticalSectionId/submissions/new`}
        component={PracticalSectionSubmissionPage}
      />
      <Route path={`${basePath}/exam_practical_sections/:sectionId`} component={PracticalSectionPage} />
      <Route
        path={`${basePath}/writing_section_submissions/:code`}
        render={() => <RedirectToRoot isSignedIn={isSignedIn} />}
      />
      <Route
        path={`${basePath}/reviewer/exam_practical_section_submissions/:submissionToken`}
        component={ExamPracticalSectionShowPageForReviewer}
      />
      <Route path={`${basePath}/application`} component={ApplicationPage} />
      <Route path={`${basePath}/application_pages/:page_key`} component={CustomApplicationPage} />
      <Route path={`${basePath}/apply/multicreator`} component={MulticreatorApplicationPage} />
      <Route path={`${basePath}/apply/beauty`} component={BeautyApplicationPage} />
      <Route path={`${basePath}/apply/money`} component={MoneyApplicationPage} />
      <Route path={`${basePath}/sign_out`} component={SignOutPage} />
      <Route path={`${basePath}/sign_in`} component={SignInPage} />
      <Route path={`${basePath}/timesheet`} component={TimesheetPage} />
      <Route path={`${basePath}/reviewer/course_work_submissions`} component={CourseWorkSubmissionListPage} />
      <Route
        path={`${basePath}/reviewer/lesson_homework_submissions/:submissionToken`}
        component={LessonHomeworkSubmissionShowPageForReviewer}
      />
      <Route path={`${basePath}/migration`} component={MigrationPage} />
      <Route path={`${basePath}/faq`} component={FaqPage} />
      <Route path={`${basePath}/pricing`} component={PricingPage} />
      <Route path={`${basePath}/payments/:code/receipt`} component={ReceiptView} />
      <Route path={`${basePath}/membership_plans/:membership_item_id/update`} component={MembershipPlanUpdatePage} />
      <Route path={`${basePath}/home_location/update`} component={HomeLocationUpdatePage} />
      <Route path={`${basePath}/attendance`} component={AttendancePage} />
      <Route
        path={`${basePath}/event_reservations/:eventReservationId/attendance/complete`}
        component={EventReservationAttendanceCompletePage}
      />
      <Route
        path={`${basePath}/reservations/:reservationId/attendance/complete`}
        component={ReservationAttendanceCompletePage}
      />
      <Route path={`${basePath}/surveys/:surveyKey`} component={SurveyShowPage} />
      <Route path={`${basePath}/withdrawal/cancel`} component={WithdrawalCancelPage} />
      <Route
        exact
        path={`${basePath}/subscriptions/:subscriptionCode/suspension`}
        component={SubscriptionSuspensionInfoPage}
      />
      <Route
        path={`${basePath}/subscriptions/:subscriptionCode/suspend`}
        component={SubscriptionSuspensionCreatePage}
      />
      <Route
        path={`${basePath}/subscriptions/:subscriptionCode/suspended`}
        component={SubscriptionSuspensionConfirmationPage}
      />
      <Route
        exact
        path={`${basePath}/subscriptions/:subscriptionCode/suspension/cancel`}
        component={SubscriptionSuspensionCancelInfoPage}
      />
      <Route path={`${basePath}/withdrawal/form`} component={WithdrawalSurveyShowPage} />
      <Route path={`${basePath}/withdrawal/complete`} component={WithdrawalCompletePage} />
      <Route path={`${basePath}/withdrawal`} component={WithdrawalApplyPage} />
      <Route path={`${basePath}/notifications/:notificationToken`} component={NotificationDeliveryShowPage} />
      <Route path={`${basePath}/notifications`} component={NotificationDeliveryListPage} />
      <RedirectToRoot isSignedIn={isSignedIn} />
    </Switch>
  );
};

export class MainAppV3 extends React.Component {
  navigationState = new NavigationState();
  coachSkillCertificationState = new CoachSkillCertificationState();

  constructor(props) {
    super(props);

    makeObservable(this, {
      navigationState: observable,
      coachSkillCertificationState: observable,
    });
  }

  async componentDidMount() {
    await this.coachSkillCertificationState.init();
    await GlobalStatesV3.currentUser.reloadUser();
  }

  render() {
    const pathname = GlobalStatesV3.routing.location.pathname;
    const sideNaviEnable =
      this.navigationState.currentType === NavigationType.SIDE && GlobalStatesV3.viewport.isDesktop;
    if (pathname.startsWith('/v3')) {
      GlobalStatesV3.routing.push(pathname.replace('/v3', ''));
    }
    const isEnabledNewDesktopLayoutForSideNavigation =
      GlobalStatesV3.viewport.isDesktop && this.navigationState.currentType === NavigationType.SIDE;
    const isEnabledPreviousDesktopLayout =
      GlobalStatesV3.viewport.isDesktop && this.navigationState.currentType !== NavigationType.SIDE;

    return (
      <React.Fragment>
        <Provider navigationState={this.navigationState} coachSkillState={this.coachSkillCertificationState}>
          {(isEnabledPreviousDesktopLayout || GlobalStatesV3.viewport.isMobile) && <Navigation />}
          <Notifier userState={GlobalStatesV3.currentUser} />
          <div styleName={classNames({ content: sideNaviEnable })}>
            {isEnabledNewDesktopLayoutForSideNavigation && <Navigation />}
            <div styleName="main-content">
              {isEnabledNewDesktopLayoutForSideNavigation && <DesktopHeader />}
              <MainAppV3Routes />
            </div>
          </div>
          <Footer />
        </Provider>
      </React.Fragment>
    );
  }
}

export default observer(MainAppV3);
